import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  User, Mail, Phone, Building2, Shield, Clock, 
  Calendar, Target, Award, TrendingUp, Edit,
  MapPin, Briefcase, Activity, CheckCircle
} from 'lucide-react';

const ViewAgentModal = ({ isOpen, onClose, agent, onEdit }) => {
  if (!agent) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'busy': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'away': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Agent Details"
      size="xl"
    >
      <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="space-y-6">
            {/* Agent Header */}
            <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-sm">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{agent.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{agent.email}</p>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={`${getStatusColor(agent.status)} text-xs font-medium`}>
                    {agent.status}
                  </Badge>
                  {agent.employeeId && (
                    <span className="text-xs text-gray-500 font-medium">ID: {agent.employeeId}</span>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onEdit(agent);
                  onClose();
                }}
                className="flex items-center gap-2 border-amber-200 text-amber-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            </div>

            {/* Personal Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <User className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900 text-lg">Personal Information</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium mb-1">Email Address</p>
                    <p className="text-sm font-medium text-gray-900 break-words">{agent.email}</p>
                  </div>
                </div>
                {agent.phone && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Phone Number</p>
                      <p className="text-sm font-medium text-gray-900">{agent.phone}</p>
                    </div>
                  </div>
                )}
                {agent.bio && (
                  <div className="col-span-2 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 font-medium mb-2">Bio / Description</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{agent.bio}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Work Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <Building2 className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900 text-lg">Work Information</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {agent.department && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Building2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Department</p>
                      <p className="text-sm font-medium text-gray-900">{agent.department}</p>
                    </div>
                  </div>
                )}
                {agent.role && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Role</p>
                      <p className="text-sm font-medium text-gray-900">{agent.role}</p>
                    </div>
                  </div>
                )}
                {agent.jobTitle && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Briefcase className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Job Title</p>
                      <p className="text-sm font-medium text-gray-900">{agent.jobTitle}</p>
                    </div>
                  </div>
                )}
                {agent.manager && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <User className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Manager</p>
                      <p className="text-sm font-medium text-gray-900">{agent.manager}</p>
                    </div>
                  </div>
                )}
                {agent.timezone && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Timezone</p>
                      <p className="text-sm font-medium text-gray-900">{agent.timezone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Skills */}
            {agent.skills && agent.skills.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <Target className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900 text-lg">Skills</h4>
                </div>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                  {agent.skills.map((skill, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1 text-sm font-medium"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Metrics */}
            {(agent.performance || agent.workload) && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900 text-lg">Performance & Workload</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {agent.workload && (
                    <>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="h-4 w-4 text-blue-600" />
                          <p className="text-xs text-gray-600 font-medium">Current Tickets</p>
                        </div>
                        <p className="text-2xl font-bold text-blue-900 mb-1">
                          {agent.workload.currentTickets || 0}
                        </p>
                        <p className="text-xs text-gray-500">
                          Max: {agent.workload.maxTickets || 15}
                        </p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <p className="text-xs text-gray-600 font-medium">Utilization</p>
                        </div>
                        <p className="text-2xl font-bold text-green-900">
                          {agent.workload.utilization || 0}%
                        </p>
                      </div>
                    </>
                  )}
                  {agent.performance && (
                    <>
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-purple-600" />
                          <p className="text-xs text-gray-600 font-medium">Avg Resolution Time</p>
                        </div>
                        <p className="text-2xl font-bold text-purple-900">
                          {agent.performance.avgResolutionTime || 0}h
                        </p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="h-4 w-4 text-amber-600" />
                          <p className="text-xs text-gray-600 font-medium">Tickets Resolved</p>
                        </div>
                        <p className="text-2xl font-bold text-amber-900">
                          {agent.performance.ticketsResolved || 0}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer Actions - Fixed at Bottom */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="px-6"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              onEdit(agent);
              onClose();
            }}
            className="flex items-center gap-2 px-6 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Edit className="h-4 w-4" />
            Edit Agent
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ViewAgentModal;

